import {
  ResolveContextRequest,
  ResolveContextResponse,
} from './hooks/useFeedbackContext';

/**
 * The transport for the ticket page's one context read.
 *
 * A REST route rather than a GraphQL field because it is not a data query the cache should ever serve:
 * this read is also the existence check, and a cached `empty` would suppress the form on a later visit
 * for a ticket that has since been analysed. `fetch` with no cache is the honest shape.
 */
export const RESOLVE_CONTEXT_ROUTE =
  '/api/agentHawk/merlin-feedback/resolve-context';

/**
 * Never throws for a server-side failure — it maps one to `status: 'error'`.
 *
 * That mapping is the whole point of the three-valued status: a rejected promise and a `status: 'empty'`
 * would otherwise be indistinguishable to the caller, and they mean opposite things. `empty` is a proven
 * absence and suppresses the form; `error` is ignorance and must not.
 *
 * A transport failure — `fetch` itself rejecting — still propagates. The caller treats that the same as
 * `error`, so both land on "existence unknown" rather than on a proven absence.
 */
export async function resolveMerlinContext(
  request: ResolveContextRequest
): Promise<ResolveContextResponse> {
  const response = await fetch(RESOLVE_CONTEXT_ROUTE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    /*
     * `include`, matching every other authenticated fetch in this website — including
     * `writeEventToMemory`, which is the other half of this same feature.
     *
     * This route reaches AgentCore Memory on behalf of the caller, so it needs the Midway session cookie.
     * `same-origin` withholds that cookie the moment the BFF is reached from a subtly different origin or
     * through the auth redirect flow, and the failure is a 401 that this client maps to `error` — meaning
     * "existence unknown", which withholds the form. So the symptom would be a feedback page that never
     * appears, on a request the rest of the application authenticates without trouble.
     */
    credentials: 'include',
  });

  if (!response.ok) return { status: 'error' };

  // An `ok` response can still carry an empty or truncated body, which rejects here. That is ignorance
  // about the analysis, not proof of its absence, so it maps to `error` like any other read failure.
  let body: ResolveContextResponse | undefined;
  try {
    body = (await response.json()) as ResolveContextResponse;
  } catch {
    return { status: 'error' };
  }

  // A body that does not name one of the three statuses is treated as ignorance rather than absence.
  // Guessing `empty` here would turn a route regression into a fleet-wide `analysis-not-found`.
  if (
    !body ||
    (body.status !== 'events' &&
      body.status !== 'empty' &&
      body.status !== 'error')
  ) {
    return { status: 'error' };
  }
  return body;
}
